import React, { useState } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import DeselectIcon from '@mui/icons-material/Deselect'
import SaveIcon from '@mui/icons-material/Save'
import { VtrItem } from '../../hooks/useVartreeEditor'

interface VartreeEditorProps {
  items: VtrItem[]
  selectedItems: Set<number>
  isSaving: boolean
  error: string | null
  onToggleSelection: (index: number) => void
  onSelectAll: (filter?: 'variables' | 'folders') => void
  onClearSelection: () => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onRemoveSelected: (deleteFiles: boolean) => void
  onRemoveFolders: (deleteFiles: boolean) => void
  onCreateFolder: (path: string) => void
  onSave: () => void
  getSelectedVariableNames: () => string[]
  getSelectedFolderPaths: () => string[]
}

const VartreeEditor: React.FC<VartreeEditorProps> = ({
  items,
  selectedItems,
  isSaving,
  error,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onMoveUp,
  onMoveDown,
  onRemoveSelected,
  onRemoveFolders,
  onCreateFolder,
  onSave,
  getSelectedVariableNames,
  getSelectedFolderPaths,
}) => {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    message: string
    action: () => void
    destructive?: boolean
  }>({ open: false, title: '', message: '', action: () => {} })

  const [folderDialog, setFolderDialog] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [parentFolderPath, setParentFolderPath] = useState('')

  const selectedVarNames = getSelectedVariableNames()
  const selectedFolderPaths = getSelectedFolderPaths()
  const hasSelectedVariables = selectedVarNames.length > 0
  const hasSelectedFolders = selectedFolderPaths.length > 0
  const hasSelection = selectedItems.size > 0

  const handleRemoveFromTree = () => {
    if (hasSelectedFolders) {
      setConfirmDialog({
        open: true,
        title: 'Remove from Vartree',
        message: `Remove ${selectedFolderPaths.length} folder(s) and ${selectedVarNames.length} variable(s) from the variable tree? The data files will remain in storage.`,
        action: () => {
          if (hasSelectedFolders) onRemoveFolders(false)
          if (hasSelectedVariables && !hasSelectedFolders) onRemoveSelected(false)
          setConfirmDialog(prev => ({ ...prev, open: false }))
        },
      })
    } else if (hasSelectedVariables) {
      setConfirmDialog({
        open: true,
        title: 'Remove from Vartree',
        message: `Remove ${selectedVarNames.length} variable(s) from the variable tree? The data files will remain in storage.`,
        action: () => {
          onRemoveSelected(false)
          setConfirmDialog(prev => ({ ...prev, open: false }))
        },
      })
    }
  }

  const handleDeletePermanently = () => {
    const totalItems = selectedVarNames.length + (hasSelectedFolders ? selectedFolderPaths.length : 0)
    setConfirmDialog({
      open: true,
      title: 'Delete Permanently',
      message: `Permanently delete ${selectedVarNames.length} variable(s)${hasSelectedFolders ? ` and ${selectedFolderPaths.length} folder(s)` : ''}? This will remove them from the variable tree AND delete the .met and .cd files from Azure storage. This cannot be undone.`,
      action: () => {
        if (hasSelectedFolders) onRemoveFolders(true)
        if (hasSelectedVariables && !hasSelectedFolders) onRemoveSelected(true)
        setConfirmDialog(prev => ({ ...prev, open: false }))
      },
      destructive: true,
    })
  }

  const handleCreateFolder = () => {
    const path = parentFolderPath
      ? `${parentFolderPath}/${newFolderName}`
      : newFolderName
    onCreateFolder(path)
    setFolderDialog(false)
    setNewFolderName('')
    setParentFolderPath('')
  }

  // Find minimum depth for indentation offset
  const minDepth = items.length > 0
    ? Math.min(...items.map(i => i.depth))
    : 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
        <Tooltip title="Select all variables">
          <IconButton size="small" onClick={() => onSelectAll('variables')}>
            <SelectAllIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear selection">
          <IconButton size="small" onClick={onClearSelection} disabled={!hasSelection}>
            <DeselectIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Remove from tree (keep files)">
          <span>
            <IconButton
              size="small"
              onClick={handleRemoveFromTree}
              disabled={!hasSelection || isSaving}
              color="warning"
            >
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete permanently (remove files)">
          <span>
            <IconButton
              size="small"
              onClick={handleDeletePermanently}
              disabled={!hasSelection || isSaving}
              color="error"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Create folder">
          <IconButton size="small" onClick={() => setFolderDialog(true)} disabled={isSaving}>
            <CreateNewFolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Save reordering">
          <span>
            <IconButton size="small" onClick={onSave} disabled={isSaving}>
              <SaveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {isSaving && <CircularProgress size={16} />}
      </Box>

      {/* Selection summary */}
      {hasSelection && (
        <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {selectedVarNames.length > 0 && (
            <Chip
              size="small"
              label={`${selectedVarNames.length} variable${selectedVarNames.length !== 1 ? 's' : ''}`}
              color="info"
              variant="outlined"
            />
          )}
          {selectedFolderPaths.length > 0 && (
            <Chip
              size="small"
              label={`${selectedFolderPaths.length} folder${selectedFolderPaths.length !== 1 ? 's' : ''}`}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Tree items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {items.map((item, index) => {
          const isSelected = selectedItems.has(index)
          const indent = (item.depth - minDepth) * 20

          return (
            <Box
              key={`${index}-${item.name}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: `${indent}px`,
                py: 0.25,
                pr: 0.5,
                bgcolor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                borderRadius: 0.5,
                cursor: 'pointer',
                minHeight: 32,
              }}
              onClick={() => onToggleSelection(index)}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => onToggleSelection(index)}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.25, mr: 0.5 }}
              />
              {item.isFolder ? (
                item.isExpanded ? (
                  <FolderOpenIcon sx={{ fontSize: 18, color: 'warning.main', mr: 0.5 }} />
                ) : (
                  <FolderIcon sx={{ fontSize: 18, color: 'warning.main', mr: 0.5 }} />
                )
              ) : (
                <Box sx={{ width: 18, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: item.isFolder ? 600 : 400,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.name}
                {item.info && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: 'text.secondary', ml: 1, fontSize: 12 }}
                  >
                    {item.info}
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0 }}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onMoveUp(index) }}
                  sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onMoveDown(index) }}
                  sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          )
        })}
      </Box>

      {/* Confirm dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.action}
            color={confirmDialog.destructive ? 'error' : 'info'}
            variant="contained"
          >
            {confirmDialog.destructive ? 'Delete Permanently' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create folder dialog */}
      <Dialog open={folderDialog} onClose={() => setFolderDialog(false)}>
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <TextField
            label="Parent folder path (optional)"
            placeholder="e.g. Demographics"
            fullWidth
            size="small"
            value={parentFolderPath}
            onChange={(e) => setParentFolderPath(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            helperText="Leave empty for top-level folder. Use / for nesting."
          />
          <TextField
            label="Folder name"
            fullWidth
            size="small"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) handleCreateFolder()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
            variant="contained"
            color="info"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VartreeEditor
